import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Overview from './pages/Overview';
import CallsExplorer from './pages/CallsExplorer';
import CallDetail from './pages/CallDetail';
import Agents from './pages/Agents';
import Queues from './pages/Queues';
import Login from './pages/Login';
import './App.css';

const TemporalAnalytics = lazy(() => import('./pages/TemporalAnalytics'));
const DurationAnalytics = lazy(() => import('./pages/DurationAnalytics'));
const ConversationAnalytics = lazy(() => import('./pages/ConversationAnalytics'));
const AgentDetail = lazy(() => import('./pages/AgentDetail'));
const QueueDetail = lazy(() => import('./pages/QueueDetail'));
const Forecast = lazy(() => import('./pages/Forecast'));
const AnalysisOverview = lazy(() => import('./pages/AnalysisOverview'));
const ComplianceHeatmap = lazy(() => import('./pages/cumplimiento'));
const BoletaImprimible = lazy(() => import('./pages/BoletaImprimible'));
const InicioImprimible = lazy(() => import('./pages/InicioImprimible'));
const EquipoImprimible = lazy(() => import('./pages/EquipoImprimible'));
const ConversionDashboard = lazy(() => import('./pages/ConversionDashboard'));
const AgentDashboard = lazy(() => import('./pages/AgentDashboard'));
const Inicio = lazy(() => import('./pages/Inicio'));
const AgentCallDetail = lazy(() => import('./pages/AgentCallDetail'));
const Customers = lazy(() => import('./pages/Customers'));
const CustomerProfile = lazy(() => import('./pages/CustomerProfile'));
const LibraryChat = lazy(() => import('./pages/LibraryChat'));
const UsefulnessAnalytics = lazy(() => import('./pages/UsefulnessAnalytics'));
const SalesReport = lazy(() => import('./pages/SalesReport'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const Users = lazy(() => import('./pages/Users'));
const Coaching = lazy(() => import('./pages/Coaching'));
const Conocimiento = lazy(() => import('./pages/Conocimiento'));
const CoberturaCoaching = lazy(() => import('./pages/CoberturaCoaching'));

function AdminShell({ children }) {
  return (
    <ProtectedRoute allow="admin">
      <Layout variant="admin">{children}</Layout>
    </ProtectedRoute>
  );
}

function AgentShell({ children }) {
  return (
    <ProtectedRoute allow="agent">
      <Layout variant="agent">{children}</Layout>
    </ProtectedRoute>
  );
}

// "Ver como agente" (admin, solo lectura): renderiza el módulo del agente en su
// propio shell, con los datos acotados al agente de la URL (:name) vía scopeAgent.
function PreviewRoute({ Comp, viewerMode }) {
  const { name } = useParams();
  const agent = name ? decodeURIComponent(name) : '';
  return (
    <ProtectedRoute allow="admin">
      <Layout variant="agent" previewAgent={agent}>
        <Comp scopeAgent={agent} viewerMode={viewerMode} />
      </Layout>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<div className="loading">Cargando...</div>}>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />

          {/* Authenticated, no role restriction — both admin y agent pueden rotar su password. */}
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />

          {/* La boleta imprimible (#98). Sin `AdminShell` ni `AgentShell`: la
              barra lateral se imprimiría. La MISMA URL para los dos roles a
              propósito —el supervisor y el agente tienen que estar leyendo el
              mismo papel— y el backend hace valer la propiedad de la llamada:
              `get_call_detail` responde 403 si un agente pide una ajena. */}
          <Route
            path="/calls/:id/boleta"
            element={<ProtectedRoute><BoletaImprimible /></ProtectedRoute>}
          />

          {/* El Inicio como documento ("Descargar PDF"). Fuera de los shells
              por la misma razón que la boleta: la barra lateral se imprimiría.
              Una ruta por rol: el ejecutivo pide el suyo, el supervisor el del
              agente por nombre; el backend hace valer que un agente no pida
              uno ajeno. */}
          <Route
            path="/agent/inicio/pdf"
            element={<ProtectedRoute allow="agent"><InicioImprimible /></ProtectedRoute>}
          />
          <Route
            path="/agents/:name/inicio/pdf"
            element={<ProtectedRoute allow="admin"><InicioImprimible /></ProtectedRoute>}
          />
          {/* El PDF de los agentes que el supervisor elige en Overview: portada
              del equipo + la MISMA hoja que descarga cada ejecutivo. */}
          <Route
            path="/equipo/pdf"
            element={<ProtectedRoute allow="admin"><EquipoImprimible /></ProtectedRoute>}
          />

          {/* Agent routes (role=agent only) */}
          {/* `/agent` es el INICIO desde el 23-sep-2026: lo primero que ve el
              ejecutivo es cómo le fue, no la lista de llamadas. La lista se
              movió a `/agent/llamadas`. */}
          <Route path="/agent" element={<AgentShell><Inicio /></AgentShell>} />
          <Route path="/agent/llamadas" element={<AgentShell><AgentDashboard /></AgentShell>} />
          <Route path="/agent/call/:id" element={<AgentShell><AgentCallDetail /></AgentShell>} />
          <Route path="/agent/me" element={<AgentShell><AgentDetail viewerMode="agent" /></AgentShell>} />
          <Route path="/agent/customers" element={<AgentShell><Customers viewerMode="agent" /></AgentShell>} />
          <Route path="/agent/customers/:id" element={<AgentShell><CustomerProfile viewerMode="agent" /></AgentShell>} />
          <Route path="/agent/compliance" element={<AgentShell><ComplianceHeatmap viewerMode="agent" /></AgentShell>} />
          <Route path="/agent/coaching" element={<AgentShell><Coaching viewerMode="agent" /></AgentShell>} />
          <Route path="/agent/conocimiento" element={<AgentShell><Conocimiento /></AgentShell>} />
          <Route path="/agent/conversion" element={<AgentShell><ConversionDashboard viewerMode="agent" /></AgentShell>} />
          <Route path="/agent/library/chat" element={<AgentShell><LibraryChat viewerMode="agent" /></AgentShell>} />

          {/* Admin routes (role=admin only) */}
          <Route path="/" element={<AdminShell><Overview /></AdminShell>} />
          <Route path="/calls" element={<AdminShell><CallsExplorer /></AdminShell>} />
          <Route path="/calls/:id" element={<AdminShell><CallDetail /></AdminShell>} />
          <Route path="/agents" element={<AdminShell><Agents /></AdminShell>} />
          <Route path="/agents/:name" element={<AdminShell><AgentDetail /></AdminShell>} />
          <Route path="/agents/:name/ver" element={<PreviewRoute Comp={Inicio} />} />
          <Route path="/agents/:name/ver/llamadas" element={<PreviewRoute Comp={AgentDashboard} />} />
          <Route path="/agents/:name/ver/me" element={<PreviewRoute Comp={AgentDetail} viewerMode="agent" />} />
          <Route path="/agents/:name/ver/clientes" element={<PreviewRoute Comp={Customers} viewerMode="agent" />} />
          <Route path="/agents/:name/ver/cumplimiento" element={<PreviewRoute Comp={ComplianceHeatmap} viewerMode="agent" />} />
          <Route path="/agents/:name/ver/conversion" element={<PreviewRoute Comp={ConversionDashboard} viewerMode="agent" />} />
          {/* Las dos que faltaban. "Ver como agente" existe para saber qué ve
              el agente, y le faltaba justo lo último que se le construyó: su
              coaching y la consulta de conocimiento. */}
          <Route path="/agents/:name/ver/coaching" element={<PreviewRoute Comp={Coaching} viewerMode="agent" />} />
          <Route path="/agents/:name/ver/conocimiento" element={<PreviewRoute Comp={Conocimiento} viewerMode="agent" />} />
          {/* El chat de la biblioteca. Se incluye porque el agente REAL lo
              tiene en su menú y esta pantalla existe para enseñar lo que él ve.
              Para el supervisor no expone nada nuevo — ya puede consultarlo
              como admin. Lo que sí expone es que ese chat NO acota por agente:
              ver la tarjeta "El chat de biblioteca no aísla por agente". */}
          <Route path="/agents/:name/ver/biblioteca" element={<PreviewRoute Comp={LibraryChat} viewerMode="agent" />} />
          <Route path="/queues" element={<AdminShell><Queues /></AdminShell>} />
          <Route path="/queues/:name" element={<AdminShell><QueueDetail /></AdminShell>} />
          <Route path="/analytics/temporal" element={<AdminShell><TemporalAnalytics /></AdminShell>} />
          <Route path="/analytics/duration" element={<AdminShell><DurationAnalytics /></AdminShell>} />
          <Route path="/analytics/conversation" element={<AdminShell><ConversationAnalytics /></AdminShell>} />
          <Route path="/analytics/forecast" element={<AdminShell><Forecast /></AdminShell>} />
          <Route path="/analytics/analysis" element={<AdminShell><AnalysisOverview /></AdminShell>} />
          <Route path="/analytics/compliance" element={<AdminShell><ComplianceHeatmap /></AdminShell>} />
          <Route path="/analytics/usefulness" element={<AdminShell><UsefulnessAnalytics /></AdminShell>} />
          <Route path="/conversion" element={<AdminShell><ConversionDashboard /></AdminShell>} />
          <Route path="/customers" element={<AdminShell><Customers /></AdminShell>} />
          <Route path="/customers/:id" element={<AdminShell><CustomerProfile /></AdminShell>} />
          <Route path="/library/chat" element={<AdminShell><LibraryChat /></AdminShell>} />
          <Route path="/reports/sales" element={<AdminShell><SalesReport /></AdminShell>} />
          <Route path="/coaching" element={<AdminShell><Coaching /></AdminShell>} />
          <Route path="/coaching/cobertura" element={<AdminShell><CoberturaCoaching /></AdminShell>} />
          <Route path="/conocimiento" element={<AdminShell><Conocimiento /></AdminShell>} />
          <Route path="/users" element={<AdminShell><Users /></AdminShell>} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
